/**
 * Keyboard navigation module for epub.js spine iframes.
 * Registered via rendition.hooks.content.register(applyKeyboardNav).
 *
 * Bindings (per Phase 6 D2):
 *   ArrowLeft / PageUp                → rendition.prev()
 *   ArrowRight / Space / PageDown     → rendition.next()
 *   Home                              → rendition.display(first spine)
 *   End                               → rendition.display(last spine)
 *
 * Space triggers next only when the target is NOT an editable element —
 * standard reader convention (avoid hijacking space in text inputs,
 * even though spine XHTML rarely has them).
 *
 * Prevents default on captured keys so the browser doesn't scroll the
 * iframe's contentWindow. Top-level keyboard shortcuts handled by
 * useDrmProtection (Ctrl+C etc.) still fire because we only prevent
 * the specific keys we handle and bail on modifier combos.
 */

interface EpubJsContents {
  document: Document;
  window: Window;
}

interface RenditionLike {
  prev(): Promise<void>;
  next(): Promise<void>;
  book: {
    spine: {
      first(): { href: string };
      last(): { href: string };
    };
  };
  display(target?: string): Promise<void>;
}

function isEditable(t: EventTarget | null): boolean {
  if (!t || !(t instanceof Element)) return false;
  const tag = t.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (t as HTMLElement).isContentEditable === true;
}

export function createKeyboardNavHandler(rendition: RenditionLike) {
  return function applyKeyboardNav(contents: EpubJsContents): void {
    const doc = contents.document;

    function onKeyDown(e: KeyboardEvent) {
      // Bail on modifier combos; those are reserved for DRM hook + browser.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'PageUp':
          e.preventDefault();
          rendition.prev();
          return;
        case 'ArrowRight':
        case 'PageDown':
          e.preventDefault();
          rendition.next();
          return;
        case ' ': // Space
          if (isEditable(e.target)) return;
          e.preventDefault();
          rendition.next();
          return;
        case 'Home':
          e.preventDefault();
          try {
            rendition.display(rendition.book.spine.first().href);
          } catch { /* spine empty or API drift */ }
          return;
        case 'End':
          e.preventDefault();
          try {
            rendition.display(rendition.book.spine.last().href);
          } catch { /* as above */ }
          return;
      }
    }

    doc.addEventListener('keydown', onKeyDown);
  };
}
